import { type CSSProperties, useMemo } from 'react';

import { useChatSidebarStore } from '@air/feature-ag-ui-chat';

import { useEditCanvasStore } from '../store';

/**
 * Returns a style object that shifts the toolbar left by half the chat sidebar
 * width when the sidebar is open and the chat panel is rendered. The sidebar
 * is absent in fullscreen mode and in read-only mode without showChatInReadOnly.
 *
 * Uses the `--chat-sidebar-width` CSS variable (set on document.documentElement
 * by SidebarResizer) so the offset stays in sync with sidebar resizing.
 */
export const useToolbarOffset = (): CSSProperties | undefined => {
  const sidebarOpen = useChatSidebarStore((state) => state.open);
  const isFullscreen = useEditCanvasStore((state) => state.isFullscreen);
  const readOnly = useEditCanvasStore((state) => state.readOnly);
  const showChatInReadOnly = useEditCanvasStore((state) => state.showChatInReadOnly);

  return useMemo(() => {
    if (!sidebarOpen || isFullscreen || (readOnly && !showChatInReadOnly)) {
      return {
        margin: '8px 16px',
        transition: 'transform 300ms ease',
      };
    }

    // ReactFlow's .react-flow__panel.bottom.center CSS applies
    // `translateX(-15px) translateX(-50%)` for centering. The inline style
    // overrides this, so we must reproduce it and append our sidebar offset.
    // Verified against @xyflow/react dist/style.css — if the library changes
    // its Panel positioning, this transform chain needs updating.
    return {
      margin: '8px 16px',
      transform: 'translateX(-15px) translateX(-50%) translateX(calc(var(--chat-sidebar-width, 400px) / -2))',
      transition: 'transform 300ms ease',
    };
  }, [isFullscreen, readOnly, showChatInReadOnly, sidebarOpen]);
};
